import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, Plus, DollarSign, Target, Trash2, AlertCircle } from "lucide-react";

const SCOPE_LEVEL_COPY = {
  "worker+task+worksite": "Worker + Task + Worksite",
  "worker+task": "Worker + Task",
  "worker+worksite": "Worker + Worksite",
  "task+worksite": "Task + Worksite",
  "worker": "Worker",
  "task": "Task",
  "worksite": "Worksite",
  "tenant_default": "Tenant default",
};

function scopeSummary(scope) {
  if (!scope) return "Tenant default";
  const bits = [];
  if (scope.worker_id) bits.push(`Worker · ${scope.worker_id.slice(0, 8)}`);
  if (scope.task_id) bits.push(`Task · ${scope.task_id.slice(0, 8)}`);
  if (scope.worksite_id) bits.push(`Worksite · ${scope.worksite_id.slice(0, 8)}`);
  return bits.length ? bits.join(" + ") : "Tenant default";
}

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch (_e) {
    return `${amount} ${currency || ""}`.trim();
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
}


export default function RateCardsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ worker_id: "", task_id: "", worksite_id: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_inactive: true };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/tenant/rate-cards", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doDelete = async (card) => {
    try {
      await api.delete(`/tenant/rate-cards/${card.id}`);
      toast.success("Rate card deactivated");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="rate-cards-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <DollarSign className="h-6 w-6" /> Rate cards
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Set bill and pay rates per worker, task, worksite — or any combination.
            Approvals freeze the resolved amounts onto each time entry, so later
            edits don't rewrite past payroll.
          </p>
        </div>
        <NewRateDialog onCreated={load} />
      </div>

      <ResolvePreview />

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>All rate cards</CardTitle>
            <CardDescription>Most-specific scopes win when resolving a rate.</CardDescription>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Worker id</Label>
              <Input
                value={filters.worker_id}
                onChange={(e) => setFilters((f) => ({ ...f, worker_id: e.target.value }))}
                placeholder="worker-id"
                className="h-9 w-40"
                data-testid="filter-worker-id"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Task id</Label>
              <Input
                value={filters.task_id}
                onChange={(e) => setFilters((f) => ({ ...f, task_id: e.target.value }))}
                placeholder="task-id"
                className="h-9 w-40"
                data-testid="filter-task-id"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Worksite id</Label>
              <Input
                value={filters.worksite_id}
                onChange={(e) => setFilters((f) => ({ ...f, worksite_id: e.target.value }))}
                placeholder="worksite-id"
                className="h-9 w-40"
                data-testid="filter-worksite-id"
              />
            </div>
            <Button variant="outline" onClick={load} data-testid="apply-filters-btn">
              Apply
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading rate cards…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="rate-cards-empty">
              No rate cards yet. The "New rate" button up top will get you started.
            </div>
          ) : (
            <Table data-testid="rate-cards-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Bill</TableHead>
                  <TableHead className="text-right">Pay</TableHead>
                  <TableHead>Effective from</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((card) => (
                  <TableRow key={card.id} data-testid={`rate-card-row-${card.id}`}>
                    <TableCell className="font-mono text-xs">{scopeSummary(card.scope)}</TableCell>
                    <TableCell className="text-right">{formatMoney(card.bill_rate, card.currency)}</TableCell>
                    <TableCell className="text-right">{formatMoney(card.pay_rate, card.currency)}</TableCell>
                    <TableCell className="text-xs">{formatDate(card.effective_from)}</TableCell>
                    <TableCell>
                      {card.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.is_active && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setConfirmDelete(card)}
                          data-testid={`rate-card-delete-${card.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="rate-card-delete-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate this rate card?</DialogTitle>
            <DialogDescription>
              Approved time entries that already used this rate keep their frozen amounts.
              Future resolutions will skip it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Keep it</Button>
            <Button variant="destructive" onClick={() => doDelete(confirmDelete)} data-testid="rate-card-delete-confirm">
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NewRateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    worker_id: "", task_id: "", worksite_id: "",
    bill_rate: "", pay_rate: "", effective_from: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        worker_id: "", task_id: "", worksite_id: "",
        bill_rate: "", pay_rate: "", effective_from: "",
      });
      setErr("");
    }
  }, [open]);

  const canSubmit = useMemo(() => {
    const bill = parseFloat(form.bill_rate);
    const pay = parseFloat(form.pay_rate);
    return !isNaN(bill) && bill >= 0 && !isNaN(pay) && pay >= 0 && !busy;
  }, [form.bill_rate, form.pay_rate, busy]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        scope: {
          worker_id: form.worker_id.trim() || null,
          task_id: form.task_id.trim() || null,
          worksite_id: form.worksite_id.trim() || null,
        },
        bill_rate: parseFloat(form.bill_rate),
        pay_rate: parseFloat(form.pay_rate),
      };
      if (form.effective_from) body.effective_from = new Date(form.effective_from).toISOString();
      await api.post("/tenant/rate-cards", body);
      toast.success("Rate card created");
      setOpen(false);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const scopeKind = useMemo(() => {
    const w = !!form.worker_id.trim();
    const t = !!form.task_id.trim();
    const s = !!form.worksite_id.trim();
    if (!w && !t && !s) return "Tenant default";
    const bits = [];
    if (w) bits.push("worker");
    if (t) bits.push("task");
    if (s) bits.push("worksite");
    return bits.join(" + ");
  }, [form.worker_id, form.task_id, form.worksite_id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-rate-btn"><Plus className="h-4 w-4" /> New rate</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl" data-testid="new-rate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" /> New rate card</DialogTitle>
          <DialogDescription>
            Leave scope fields blank for broader rates. All blank = tenant default
            (only one active at a time; a new default replaces the old one).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Worker id</Label>
              <Input
                value={form.worker_id}
                onChange={(e) => setForm((f) => ({ ...f, worker_id: e.target.value }))}
                placeholder="(any)"
                data-testid="new-rate-worker-id"
              />
            </div>
            <div className="space-y-1">
              <Label>Task id</Label>
              <Input
                value={form.task_id}
                onChange={(e) => setForm((f) => ({ ...f, task_id: e.target.value }))}
                placeholder="(any)"
                data-testid="new-rate-task-id"
              />
            </div>
            <div className="space-y-1">
              <Label>Worksite id</Label>
              <Input
                value={form.worksite_id}
                onChange={(e) => setForm((f) => ({ ...f, worksite_id: e.target.value }))}
                placeholder="(any)"
                data-testid="new-rate-worksite-id"
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="new-rate-scope-kind">
            Scope: <span className="font-medium text-foreground">{scopeKind}</span>
          </div>
          <Separator />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Bill rate (per hour)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.bill_rate}
                onChange={(e) => setForm((f) => ({ ...f, bill_rate: e.target.value }))}
                data-testid="new-rate-bill"
              />
            </div>
            <div className="space-y-1">
              <Label>Pay rate (per hour)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.pay_rate}
                onChange={(e) => setForm((f) => ({ ...f, pay_rate: e.target.value }))}
                data-testid="new-rate-pay"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Effective from (optional)</Label>
            <Input
              type="datetime-local"
              value={form.effective_from}
              onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
              data-testid="new-rate-effective"
            />
            <p className="text-xs text-muted-foreground">Blank uses current time.</p>
          </div>
          {err && (
            <Alert variant="destructive" data-testid="new-rate-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="new-rate-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResolvePreview() {
  const [q, setQ] = useState({ worker_id: "", task_id: "", worksite_id: "", at: "" });
  const [resolved, setResolved] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true); setErr(""); setResolved(null);
    try {
      const body = {
        worker_id: q.worker_id.trim() || null,
        task_id: q.task_id.trim() || null,
        worksite_id: q.worksite_id.trim() || null,
      };
      if (q.at) body.at = new Date(q.at).toISOString();
      const { data } = await api.post("/tenant/rate-cards/resolve", body);
      setResolved(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="resolve-preview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4" /> Preview resolution
        </CardTitle>
        <CardDescription>
          Enter a worker (plus optional task / worksite / timestamp) and we'll
          show you which rate card would fire — including the precedence level
          that matched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Worker id</Label>
            <Input
              value={q.worker_id}
              onChange={(e) => setQ((p) => ({ ...p, worker_id: e.target.value }))}
              placeholder="worker-id"
              data-testid="resolve-worker-id"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Task id</Label>
            <Input
              value={q.task_id}
              onChange={(e) => setQ((p) => ({ ...p, task_id: e.target.value }))}
              placeholder="(optional)"
              data-testid="resolve-task-id"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Worksite id</Label>
            <Input
              value={q.worksite_id}
              onChange={(e) => setQ((p) => ({ ...p, worksite_id: e.target.value }))}
              placeholder="(optional)"
              data-testid="resolve-worksite-id"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">At</Label>
            <Input
              type="datetime-local"
              value={q.at}
              onChange={(e) => setQ((p) => ({ ...p, at: e.target.value }))}
              data-testid="resolve-at"
            />
          </div>
        </div>
        <div>
          <Button onClick={run} disabled={busy} data-testid="resolve-run">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Resolve
          </Button>
        </div>
        {err && (
          <Alert variant="destructive" data-testid="resolve-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}
        {resolved && (
          <div className="rounded-lg border bg-muted/40 p-4 space-y-2" data-testid="resolve-result">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs text-muted-foreground">Matched at</div>
                <div className="font-medium">
                  {SCOPE_LEVEL_COPY[resolved.resolved_from?.scope_level] || resolved.resolved_from?.scope_level}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Rate card</div>
                <div className="font-mono text-xs">{resolved.resolved_from?.rate_card_id}</div>
              </div>
            </div>
            <Separator />
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-xs text-muted-foreground">Bill rate</div>
                <div className="font-semibold">{formatMoney(resolved.bill_rate, resolved.currency)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Pay rate</div>
                <div className="font-semibold">{formatMoney(resolved.pay_rate, resolved.currency)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Effective from</div>
                <div className="text-sm">{formatDate(resolved.effective_from)}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
